import { useQuery } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { getFieldLabel } from "../../../utils/getFieldLabel";
import { Crop } from "./constants";

// Route: GET /data/fields
// API Docs: https://documenter.getpostman.com/view/16381164/UVksNF2z#d4abf219-5d98-4080-bc76-f9cb7c712b34

export const getUseFieldsQueryKey = () => {
  return [`/data/fields`];
};

export const useFields = () => {
  const regularRefetchInterval = 1000 * 60;
  const fastRefetchInterval = 1000 * 2;
  return useQuery(getUseFieldsQueryKey(), {
    select: (data: FieldsResponse) => {
      const newData = cloneDeep(data);

      // Parse geometry strings
      // TODO: Remove this when backend has migrated data.
      newData.data?.fields.forEach((field) => {
        if (typeof field.geometry === "string") {
          console.warn(
            "Parsing geometry of field",
            getFieldLabel(field),
            field.id
          );
          field.geometry = JSON.parse(field.geometry) as
            | GeoJSON.Polygon
            | GeoJSON.MultiPolygon;
        }

        if (typeof field.geometry.coordinates === "string") {
          console.warn(
            "Parsing coordinates of field geometry",
            getFieldLabel(field),
            field.id
          );
          field.geometry.coordinates = JSON.parse(field.geometry.coordinates);
        }
      });

      // TODO: Remove this when backend has migrated data.
      newData.data?.fields.forEach((field) => {
        // Collapse nested soil property
        if (field.soil != null && "soil" in field.soil) {
          (field.soil as any) = field.soil.soil;
        }

        // Set field.id to field.field_id if it's missing
        if (field.id == null) {
          field.id = field.field_id;
        }

        // Set field.lat_center to 0 if it's missing
        if (field.lat_center == null) {
          field.lat_center = "0";
        }

        // Set field.lon_center to 0 if it's missing
        if (field.lon_center == null) {
          field.lon_center = "0";
        }
      });

      return newData;
    },
    refetchInterval: (data) =>
      data?.data?.fields.some((field) => field.data_status === "PROCESSING")
        ? fastRefetchInterval
        : regularRefetchInterval,
  });
};

export const fieldsCompareFn = (a: Field, b: Field): number => {
  const aLabel = getFieldLabel(a);
  const bLabel = getFieldLabel(b);

  if (aLabel != null && bLabel != null) {
    // Marknr is in the format "X-YS" where X and Y are integers and S is an optional series of letters
    const markNrRegex = /(\d+)-(\d+)([a-z]*)/;
    const aMatch = aLabel.match(markNrRegex);
    const bMatch = bLabel.match(markNrRegex);
    const aIsMarkNr = aMatch != null;
    const bIsMarkNr = bMatch != null;

    if (aIsMarkNr && bIsMarkNr) {
      const aX = parseInt(aMatch[1]);
      const bX = parseInt(bMatch[1]);
      const aY = parseInt(aMatch[2]);
      const bY = parseInt(bMatch[2]);
      const aS = aMatch[3];
      const bS = bMatch[3];

      if (aX !== bX) {
        return aX - bX;
      } else if (aY !== bY) {
        return aY - bY;
      } else {
        return aS.localeCompare(bS);
      }
    }
  }

  return aLabel.localeCompare(bLabel);
};

export type FieldsResponse = {
  messages: { status: string };
  data?: Data;
};

type Data = {
  fields: Field[];
  info: { returned_count: number };
};

export type Field = {
  /**
   * !IMPORATANT This type is intentionally incorrect.
   * Some old fields don't have an id specified.
   * So the correct type is actually:
   *
   * id?: string
   *
   * We are using the incorrect type below because this type is used in the return
   * type of the `useFields` hook up above, in which we set the id to field_id if
   * it's missing.
   *
   * TODO: Remove this comment when backend has migrated data.
   **/
  id: string;
  field_id: string;
  weather_data_sources: string[];
  /**
   * !IMPORATANT This type is intentionally incorrect.
   * Some old fields have the soil data in a nested soil prop.
   * So the correct type is actually:
   *
   * soil?:
   *   | {
   *       locations: SoilLocation[];
   *     }
   *   | {
   *       soil?: {
   *         locations: SoilLocation[];
   *       };
   *     };
   *
   * We are using the incorrect type below because this type is used in the return
   * type of the `useFields` hook up above, in which we have a select function
   * that collapses the nested soil prop.
   *
   * TODO: Remove this comment when backend has migrated data.
   **/
  soil?: {
    locations: SoilLocation[];
  };
  "consumer_id#enterprise_id": string;
  created: number;
  fm_data_source_record_id: string;
  data_status: "OK" | "PROCESSING" | "ERROR" | "PROCESSING_ERROR";
  data_status_progress?: `${string}%`; // Optional because it's only available on fields created after ca. 2022-11-24.
  fm_field_id: string;
  /**
   * !IMPORATANT This type is intentionally incorrect.
   * Some old fields don't have a lat_center specified.
   * So the correct type is actually:
   *
   * lat_center?: string;
   *
   * We are using the incorrect type below because this type is used in the return
   * type of the `useFields` hook up above, in which we set the lat_center to 0 if
   * it's missing.
   *
   * TODO: Remove this comment when backend has migrated data.
   **/
  lat_center: string;
  /**
   * !IMPORATANT This type is intentionally incorrect.
   * Some old fields don't have a lon_center specified.
   * So the correct type is actually:
   *
   * lon_center?: string;
   *
   * We are using the incorrect type below because this type is used in the return
   * type of the `useFields` hook up above, in which we set the lon_center to 0 if
   * it's missing.
   *
   * TODO: Remove this comment when backend has migrated data.
   **/
  lon_center: string;
  custom_attributes?: {
    has_irrigation_metrics_enabled?: boolean;
    app_properties?: {
      farm_name?: string; // farm_name hasn't been renamed to field_group in the backend yet
      main_id?: string;
      secondary_id?: string;
      crop_type?: Crop;
      /** UNIX epoch in seconds */
      sowing_date_expected?: number;
      /** UNIX epoch in seconds */
      harvest_date_expected?: number;
    };
    properties?: {
      Marknr?: string;
      Afgroede?: string;
      Markblok?: string;
      GBanmeldt?: number;
      Afgkode?: number;
      GB?: number;
      Journalnr?: string;
      IMK_areal?: number;
      CVR?: string;
      area?: number; // Area in square meters
      area_acres?: string; // Area in acres as a string, eg. "54.321"
      area_ha?: string; // Area in hectares as a string, eg. "12.345"
      id?: string;
    };
    total_fields_for_cvr?: number;
  };
  fm_field_record_id: number;
  /**
   * !IMPORATANT This type is intentionally incorrect.
   * Some old fields have their geometry stored as a string.
   * So the correct type is actually:
   *
   * geometry: string | GeoJSON.Point | GeoJSON.Polygon | GeoJSON.MultiPolygon;
   *
   * We are using the incorrect type below because this type is used in the return
   * type of the `useFields` hook up above, in which we have a select function
   * that parses the geometry if it's a string.
   *
   * TODO: Remove this comment when backend has migrated data.
   **/
  geometry: GeoJSON.Point | GeoJSON.Polygon | GeoJSON.MultiPolygon;
  soil_silt?: number;
  soil_clay?: number;
  soil_type?: SoilType;
  soil_sand?: number;
};

type SoilLocation = {
  coordinates: {
    lat_used_for_soil_grids: number;
    lon: number;
    lat: number;
    lon_used_for_soil_grids: number;
  };
  depths: SoilDepth[];
  id: number;
};

type SoilDepth = {
  top: number;
  bottom: number;
  fk_location: string;
  id: string;
  label: string;
  metrics: {
    calculated: Metrics;
    default: Metrics;
  };
  properties: Array<{
    name: SoilName;
    Q0_5: number;
    Q0_95: number;
    Q0_05: number;
    uncertainty: number;
    mean: number;
  }>;
};

export type SoilName = "clay" | "nitrogen" | "phh2o" | "sand" | "silt" | "soc";

type Metrics = {
  saturation_point: number;
  field_capacity: number;
  wilting_point: number;
  soil_type_name?: SoilType;
};

export const soilTypes = [
  "Sand",
  "Loamy sand",
  "Sandy loam",
  "Loam",
  "Silty loam",
  "Silt",
  "Clay loam",
  "Sandy clay loam",
  "Silty clay loam",
  "Sandy clay",
  "Silty clay",
  "Clay",
] as const;
type SoilType = (typeof soilTypes)[number];

export const isSoilType = (value: any): value is SoilType => {
  return soilTypes.includes(value);
};
